import { SearchIcon, X } from "lucide-react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import {
  useArraySearchParamFilter,
  useScalarSearchParamFilter,
} from "@/hooks/use-search-param-filter";
import { cn } from "@/lib/utils";
import SpinnerIcon from "@/ui/icons/spinner";
import { Button } from "./button";
import { TableFilter, TableSort } from "./combobox";

export function FiltersContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center w-full gap-2 px-4 py-2 overflow-x-auto", className)}>
      {children}
    </div>
  );
}

export function ResetFilters({
  title = "Reset",
  filterKeys,
}: {
  title?: string;
  filterKeys: string[];
}) {
  const searchParams = useSearchParams();

  const [ready, setReady] = useState(false);
  useEffect(() => {
    setReady(true);
  }, []);

  const activeFilterKeys = useMemo(
    () => filterKeys.filter((key) => Boolean(searchParams?.get(key))),
    [searchParams, filterKeys],
  );

  const nextUrl = useMemo(() => {
    if (!ready) {
      return null;
    }
    const url = new URL(window.location.href);
    filterKeys.forEach((key) => {
      url.searchParams.delete(key);
    });
    return url.href;
  }, [filterKeys, ready]);

  const isFiltered = activeFilterKeys.length > 0;

  if (!isFiltered) return null;

  if (!nextUrl) return null;
  return (
    <Button asChild variant="outline" size="sm" className="border-dashed">
      <Link href={nextUrl}>
        <X className="w-4 h-4 mr-2" />
        <span className="whitespace-nowrap">{title}</span>
      </Link>
    </Button>
  );
}

export function SearchFilter({
  placeholder = "Search...",
  isLoading,
}: {
  placeholder?: string;
  isLoading?: boolean;
}) {
  const [value, setValue] = useScalarSearchParamFilter("query", "");

  return (
    // On phones the search grows to fill the space left by the sibling
    // filters (flex-1) with a small floor; sm+ resets to the fixed desktop
    // width so that layout is unchanged.
    <form
      onSubmit={(e) => e.preventDefault()}
      className="flex-1 min-w-48 sm:flex-initial sm:min-w-80"
    >
      <label htmlFor="search" className="sr-only">
        Search
      </label>
      <Input
        type="search"
        id="query"
        name="query"
        className="focus:ring-0 sm:text-sm border-border"
        leadingIcon={
          isLoading ? (
            <SpinnerIcon
              className="animate-spin text-muted-foreground size-3.5"
              aria-hidden="true"
            />
          ) : (
            <SearchIcon className="size-3.5 text-muted-foreground" aria-hidden="true" />
          )
        }
        placeholder={placeholder}
        onChange={(e) => {
          setValue(e.target.value);
          e.target.focus();
        }}
        value={value ?? undefined}
      />
    </form>
  );
}

const CONTACT_STATUS_OPTIONS = [
  {
    value: "true",
    label: "Unsubscribed",
  },
  {
    value: "false",
    label: "Subscribed",
  },
];
export function ContactStatusFilter() {
  const [value, setValue] = useArraySearchParamFilter("optout");
  return (
    <TableFilter
      type="radio"
      title="Status"
      value={value}
      options={CONTACT_STATUS_OPTIONS}
      onChange={setValue}
    />
  );
}

export function ContactsSort() {
  const [value, setValue] = useArraySearchParamFilter("sort");

  const options = [
    {
      label: "Newest",
      value: "-createdAt",
    },
    {
      label: "Oldest",
      value: "createdAt",
    },
  ];

  return <TableSort title="Sort by" value={value} options={options} onChange={setValue} />;
}

const LIST_TYPE_OPTIONS = [
  {
    value: "static",
    label: "Static List",
  },
  {
    value: "dynamic",
    label: "Segment",
  },
];
export function ListTypeFilter() {
  const [value, setValue] = useArraySearchParamFilter("type");
  return (
    <TableFilter
      type="radio"
      title="Type"
      value={value}
      options={LIST_TYPE_OPTIONS}
      onChange={setValue}
    />
  );
}

export function ListSort() {
  const [value, setValue] = useArraySearchParamFilter("sort");

  const options = [
    {
      label: "Newest",
      value: "-createdAt",
    },
    {
      label: "Oldest",
      value: "createdAt",
    },
    {
      label: "List member count",
      value: "-members",
    },
  ];

  return <TableSort title="Sort by" value={value} options={options} onChange={setValue} />;
}
export function ListMemberSort() {
  const [value, setValue] = useArraySearchParamFilter("sort");

  const options = [
    {
      label: "Newest",
      value: "-createdAt",
    },
    {
      label: "Oldest",
      value: "createdAt",
    },
  ];

  return <TableSort title="Sort by" value={value} options={options} onChange={setValue} />;
}

const AUTOMATION_STATUS_OPTIONS = [
  {
    value: "true",
    label: "Active",
  },
  {
    value: "false",
    label: "Inactive",
  },
];
export function AutomationStatusFilter() {
  const [value, setValue] = useArraySearchParamFilter("active");
  return (
    <TableFilter
      type="radio"
      title="Status"
      value={value}
      options={AUTOMATION_STATUS_OPTIONS}
      onChange={setValue}
    />
  );
}

export function AutomationSort() {
  const [value, setValue] = useArraySearchParamFilter("sort");

  const options = [
    {
      label: "Newest",
      value: "-createdAt",
    },
    {
      label: "Oldest",
      value: "createdAt",
    },
  ];

  return <TableSort title="Sort by" value={value} options={options} onChange={setValue} />;
}

const CAMPAIGN_STATUS_OPTIONS = [
  {
    value: "scheduled",
    label: "Scheduled",
  },
  {
    value: "draft",
    label: "Draft",
  },
  {
    value: "queued",
    label: "Queued",
  },
  {
    value: "processing",
    label: "Processing",
  },
  {
    value: "complete",
    label: "Complete",
  },
];
export function CampaignStatusFilter() {
  const [value, setValue] = useArraySearchParamFilter("status");
  return (
    <TableFilter
      type="radio"
      title="Status"
      value={value}
      options={CAMPAIGN_STATUS_OPTIONS}
      onChange={setValue}
    />
  );
}

export function CampaignSort() {
  const [value, setValue] = useArraySearchParamFilter("sort");

  const options = [
    {
      label: "Newest",
      value: "-createdAt",
    },
    {
      label: "Oldest",
      value: "createdAt",
    },
  ];

  return <TableSort title="Sort by" value={value} options={options} onChange={setValue} />;
}

const RECIPIENT_ACTION_OPTIONS = [
  {
    value: "delivered",
    label: "Delivered",
  },
  {
    value: "failed",
    label: "Failed",
  },
  {
    value: "reply",
    label: "Replied",
  },
  {
    value: "click",
    label: "Clicked",
  },
  {
    value: "optout",
    label: "Opt out",
  },
];
export function RecipientActionFilter() {
  const [value, setValue] = useArraySearchParamFilter("action");
  return (
    <TableFilter
      type="radio"
      title="Status"
      value={value}
      options={RECIPIENT_ACTION_OPTIONS}
      onChange={setValue}
    />
  );
}

const FLOW_CONTACT_STATUS_OPTIONS = [
  {
    value: "queued",
    label: "Queued",
  },
  {
    value: "pending",
    label: "Pending",
  },
  {
    value: "waiting",
    label: "Waiting",
  },
  {
    value: "complete",
    label: "Complete",
  },
  {
    value: "failed",
    label: "Failed",
  },
];
export function FlowContactStatusFilter() {
  const [value, setValue] = useArraySearchParamFilter("status");
  return (
    <TableFilter
      type="radio"
      title="Status"
      value={value}
      options={FLOW_CONTACT_STATUS_OPTIONS}
      onChange={setValue}
    />
  );
}

const MESSAGE_TEMPLATE_TYPE_OPTIONS = [
  {
    value: "email",
    label: "Email",
  },
  {
    value: "sms",
    label: "Text message",
  },
];
export function MessageTemplateTypeFilter() {
  const [value, setValue] = useArraySearchParamFilter("type");
  return (
    <TableFilter
      type="radio"
      title="Type"
      value={value}
      options={MESSAGE_TEMPLATE_TYPE_OPTIONS}
      onChange={setValue}
    />
  );
}

export function MessageTemplateSort() {
  const [value, setValue] = useArraySearchParamFilter("sort");

  const options = [
    {
      label: "Newest",
      value: "-createdAt",
    },
    {
      label: "Oldest",
      value: "createdAt",
    },
    {
      label: "Name (A→Z)",
      value: "name",
    },
    {
      label: "Name (Z→A)",
      value: "-name",
    },
  ];

  return <TableSort title="Sort by" value={value} options={options} onChange={setValue} />;
}

export function FormSort() {
  const [value, setValue] = useArraySearchParamFilter("sort");

  const options = [
    {
      label: "Newest",
      value: "-createdAt",
    },
    {
      label: "Oldest",
      value: "createdAt",
    },
    {
      label: "Most submissions",
      value: "-submissions",
    },
    {
      label: "Least submissions",
      value: "submissions",
    },
  ];

  return <TableSort title="Sort by" value={value} options={options} onChange={setValue} />;
}

const PAGE_TYPE_OPTIONS = [
  {
    value: "form",
    label: "Form",
  },
  {
    value: "landing",
    label: "Landing page",
  },
];
export function PageTypeFilter() {
  const [value, setValue] = useArraySearchParamFilter("type");
  return (
    <TableFilter
      type="radio"
      title="Type"
      value={value}
      options={PAGE_TYPE_OPTIONS}
      onChange={setValue}
    />
  );
}

const PAGE_STATUS_OPTIONS = [
  {
    value: "published",
    label: "Live",
  },
  {
    value: "draft",
    label: "Draft",
  },
];
export function PageStatusFilter() {
  const [value, setValue] = useArraySearchParamFilter("status");
  return (
    <TableFilter
      type="radio"
      title="Status"
      value={value}
      options={PAGE_STATUS_OPTIONS}
      onChange={setValue}
    />
  );
}

const PAGE_SOURCE_OPTIONS = [
  {
    value: "agent",
    label: "Agent-created",
  },
];
export function PageSourceFilter() {
  const [value, setValue] = useArraySearchParamFilter("source");
  return (
    <TableFilter
      type="checkbox"
      title="Source"
      value={value}
      options={PAGE_SOURCE_OPTIONS}
      onChange={setValue}
    />
  );
}

export function PageSort() {
  const [value, setValue] = useArraySearchParamFilter("sort");

  const options = [
    {
      label: "Newest",
      value: "-createdAt",
    },
    {
      label: "Oldest",
      value: "createdAt",
    },
    {
      label: "Most views",
      value: "-views",
    },
    {
      label: "Least views",
      value: "views",
    },
  ];

  return <TableSort title="Sort by" value={value} options={options} onChange={setValue} />;
}
